<template>
  <v-container fluid>
    <v-layout row wrap>

      <v-flex xs12 md6>
        <skeleton-chart v-if="loading" :bar-count="4"></skeleton-chart>
        <v-card v-else class="elevation-1">
          <v-card-title class="pa-2">
            <h4 class="font-weight-regular">Card Volume by Brand</h4>
          </v-card-title>
          <v-card-text class="pa-2">
            <v-chart :option="volumeChartOptions" style="height: 350px;" autoresize />
          </v-card-text>
        </v-card>
      </v-flex>

      <v-flex xs12 md6>
        <skeleton-chart v-if="loading" :bar-count="4"></skeleton-chart>
        <v-card v-else class="elevation-1">
          <v-card-title class="pa-2">
            <h4 class="font-weight-regular">Card Transaction Count by Brand</h4>
          </v-card-title>
          <v-card-text class="pa-2">
            <v-chart :option="countChartOptions" style="height: 350px;" autoresize />
          </v-card-text>
        </v-card>
      </v-flex>

      <v-flex xs12>
        <skeleton-chart v-if="loading" :bar-count="8" show-legend></skeleton-chart>
        <v-card v-else class="elevation-1">
          <v-card-title class="pa-2">
            <h4 class="font-weight-regular">Card Volume Breakdown Over Time</h4>
          </v-card-title>
          <v-card-text class="pa-2">
            <v-chart :option="timelineChartOptions" style="height: 400px;" autoresize />
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import VChart from 'vue-echarts'
import SkeletonChart from '@/components/common/SkeletonChart'

export default {
  components: {
    VChart,
    SkeletonChart
  },

  props: {
    cardVolumeByBrand: {
      type: Object,
      default: () => ({})
    },
    merchants: {
      type: Array,
      default: () => []
    },
    selectedMerchantKsuids: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    cardBrandMetadata () {
      console.log('[AnalyticsView] cardVolumeByBrand prop:', this.cardVolumeByBrand)
      return (this.cardVolumeByBrand && this.cardVolumeByBrand.metadata) || {}
    },

    cardBrandDistribution () {
      console.log('[AnalyticsView] cardBrandMetadata:', this.cardBrandMetadata)
      const dist = this.cardBrandMetadata.distribution || {}
      console.log('[AnalyticsView] cardBrandDistribution:', dist)
      return dist
    },

    cardBrandSeries () {
      const series = (this.cardVolumeByBrand && this.cardVolumeByBrand.data && this.cardVolumeByBrand.data.series) || []
      console.log('[AnalyticsView] cardBrandSeries:', series)
      return series
    },

    volumeByBrand () {
      const volumes = {}
      this.cardBrandSeries.forEach(series => {
        const volumeData = series.volume_data || series.data || []
        const total = volumeData.reduce((sum, val) => sum + val, 0)
        volumes[series.card_type] = total
      })
      console.log('[AnalyticsView] volumeByBrand:', volumes)
      return volumes
    },

    countByBrand () {
      const counts = {}
      this.cardBrandSeries.forEach(series => {
        const countData = series.count_data || []
        if (countData.length > 0) {
          const total = countData.reduce((sum, val) => sum + val, 0)
          counts[series.card_type] = total
        }
      })
      console.log('[AnalyticsView] countByBrand:', counts)
      return counts
    },

    volumeChartOptions () {
      const brands = Object.keys(this.volumeByBrand)
      const values = Object.values(this.volumeByBrand)

      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            const param = params[0]
            const percentage = this.getPercentage(param.name)
            return `${param.name}<br/>Volume: ${this.formatCurrency(param.value)}<br/>Percentage: ${percentage}%`
          }
        },
        toolbox: {
          feature: {
            saveAsImage: { title: 'Export PNG' }
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value) => this.formatAxisCurrency(value)
          }
        },
        yAxis: {
          type: 'category',
          data: brands
        },
        series: [{
          name: 'Volume',
          type: 'bar',
          data: brands.map((brand, index) => ({
            value: values[index],
            itemStyle: {
              color: this.getBrandColor(brand),
              borderRadius: [0, 4, 4, 0]
            }
          })),
          label: {
            show: true,
            position: 'right',
            formatter: (params) => {
              const percentage = this.getPercentage(params.name)
              return `${percentage}%`
            }
          }
        }]
      }
    },

    countChartOptions () {
      const brands = Object.keys(this.countByBrand)
      const counts = Object.values(this.countByBrand)

      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            const param = params[0]
            const percentage = this.getCountPercentage(param.name)
            return `${param.name}<br/>Transactions: ${param.value.toLocaleString()}<br/>Percentage: ${percentage}%`
          }
        },
        toolbox: {
          feature: {
            saveAsImage: { title: 'Export PNG' }
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value) => value.toLocaleString()
          }
        },
        yAxis: {
          type: 'category',
          data: brands
        },
        series: [{
          name: 'Count',
          type: 'bar',
          data: brands.map((brand, index) => ({
            value: counts[index],
            itemStyle: {
              color: this.getBrandColor(brand),
              borderRadius: [0, 4, 4, 0]
            }
          })),
          label: {
            show: true,
            position: 'right',
            formatter: (params) => {
              const percentage = this.getCountPercentage(params.name)
              return `${percentage}%`
            }
          }
        }]
      }
    },

    timelineChartOptions () {
      const xAxis = (this.cardVolumeByBrand && this.cardVolumeByBrand.data && this.cardVolumeByBrand.data.x_axis) || []
      const series = this.cardBrandSeries.map(item => ({
        name: item.card_type,
        type: 'line',
        smooth: true,
        data: item.volume_data || item.data || [],
        itemStyle: {
          color: item.color || this.getBrandColor(item.card_type)
        }
      }))

      const brands = this.cardBrandSeries.map(s => s.card_type)

      return {
        tooltip: {
          trigger: 'axis',
          formatter: (params) => {
            let result = `${params[0].axisValue}<br/>`
            let total = 0
            params.forEach(param => {
              result += `${param.marker} ${param.seriesName}: ${this.formatCurrency(param.value)}<br/>`
              total += param.value
            })
            result += `<strong>Total: ${this.formatCurrency(total)}</strong>`
            return result
          }
        },
        toolbox: {
          feature: {
            saveAsImage: { title: 'Export PNG' },
            dataZoom: { title: { zoom: 'Zoom', back: 'Reset' } },
            restore: { title: 'Restore' }
          }
        },
        legend: {
          data: brands,
          bottom: 0
        },
        dataZoom: [
          { type: 'inside' },
          { type: 'slider', height: 20, bottom: 40 }
        ],
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '10%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: xAxis,
          axisLabel: {
            rotate: 45,
            formatter: (value) => {
              const date = new Date(value)
              return `${date.getMonth() + 1}/${date.getDate()}`
            }
          }
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value) => this.formatAxisCurrency(value)
          }
        },
        series: series
      }
    }
  },

  methods: {
    getPercentage (brandName) {
      const value = this.cardBrandDistribution[brandName]
      return value ? value.toFixed(1) : '0.0'
    },

    getCountPercentage (brandName) {
      if (!this.cardBrandMetadata.count_distribution) {
        return '0.0'
      }
      const value = this.cardBrandMetadata.count_distribution[brandName]
      return value ? value.toFixed(1) : '0.0'
    },

    formatCurrency (value) {
      if (typeof value === 'string') {
        value = parseFloat(value)
      }
      if (isNaN(value)) {
        return '$0.00'
      }
      return value.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },

    formatAxisCurrency (value) {
      if (value >= 1000000) {
        return '$' + (value / 1000000).toFixed(1) + 'M'
      } else if (value >= 1000) {
        return '$' + (value / 1000).toFixed(0) + 'K'
      }
      return '$' + value.toFixed(0)
    },

    getBrandColor (brand) {
      const colors = {
        VISA: '#1434CB',
        MASTERCARD: '#EB001B',
        AMEX: '#016FD0',
        DISCOVER: '#E55C20'
      }
      return colors[brand] || '#999999'
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/manage/paynetworx/funding/AnalyticsView.vue